import React from "react";
import { Check, Copy, Code, Bookmark, BookmarkPlus, Calendar, Sun, Moon, Tag, ChevronDown, Layout } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { ComponentItem } from "./types";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Toggle } from "../ui/toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Helper function to generate complete HTML with styles for copying
const generateCompleteHtml = (component: ComponentItem): string => {
  const content = component.preview;
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${component.name}</title>
  <style>
    /* Reset and base styles */
    *, *::before, *::after {
      box-sizing: border-box;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      line-height: 1.5;
      background-color: #1a1a1a;
      color: #fafafa;
    }
    
    /* Utility classes */
    .flex { display: flex; }
    .items-center { align-items: center; }
    .justify-center { justify-content: center; }
    .justify-between { justify-content: space-between; }
    .flex-col { flex-direction: column; }
    .w-full { width: 100%; }
    .h-full { height: 100%; }
    .mx-auto { margin-left: auto; margin-right: auto; }
    .rounded-lg { border-radius: 0.5rem; }
    .rounded-md { border-radius: 0.375rem; }
    .p-4 { padding: 1rem; }
    .p-3 { padding: 0.75rem; }
    .p-2 { padding: 0.5rem; }
    .p-1 { padding: 0.25rem; }
    .px-4 { padding-left: 1rem; padding-right: 1rem; }
    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .gap-2 { gap: 0.5rem; }
    .space-y-2 > * + * { margin-top: 0.5rem; }
    .font-medium { font-weight: 500; }
    .font-bold { font-weight: 700; }
    .text-sm { font-size: 0.875rem; }
    .text-xs { font-size: 0.75rem; }
    .border { border: 1px solid; }
    .mb-4 { margin-bottom: 1rem; }
    .mt-4 { margin-top: 1rem; }
    
    /* Theme variables */
    :root {
      --background: #1a1a1a;
      --foreground: #fafafa;
      --card: #242424;
      --card-foreground: #fafafa;
      --primary: #6366f1;
      --primary-foreground: #fafafa;
      --secondary: #3f3f46;
      --muted: #3f3f46;
      --muted-foreground: #a1a1aa;
      --accent: #3f3f46;
      --border: #2e2e2e;
    }
    
    /* Component styles */
    .bg-background { background-color: var(--background); }
    .bg-card { background-color: var(--card); }
    .bg-primary { background-color: var(--primary); }
    .bg-primary\\/20 { background-color: rgba(99, 102, 241, 0.2); }
    .bg-primary\\/10 { background-color: rgba(99, 102, 241, 0.1); }
    .bg-muted { background-color: var(--muted); }
    .bg-muted\\/50 { background-color: rgba(63, 63, 70, 0.5); }
    .text-foreground { color: var(--foreground); }
    .text-muted-foreground { color: var(--muted-foreground); }
    .text-primary { color: var(--primary); }
    .text-primary-foreground { color: var(--primary-foreground); }
    .border-border { border-color: var(--border); }
    .border-primary { border-color: var(--primary); }
    .border-primary\\/30 { border-color: rgba(99, 102, 241, 0.3); }
    
    /* Button styles */
    button, .button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.375rem;
      font-weight: 500;
      outline: none;
      transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      cursor: pointer;
    }
    
    button.bg-primary, .button.bg-primary {
      background-color: var(--primary);
      color: var(--primary-foreground);
    }
    
    button:hover.bg-primary, .button:hover.bg-primary {
      background-color: rgba(99, 102, 241, 0.9);
    }
    
    button.outline, .button.outline {
      background-color: transparent;
      border: 1px solid var(--border);
      color: var(--foreground);
    }
    
    button:hover.outline, .button:hover.outline {
      background-color: rgba(63, 63, 70, 0.1);
    }
    
    /* Input styles */
    input, textarea, select {
      background-color: var(--background);
      border: 1px solid var(--border);
      border-radius: 0.375rem;
      color: var(--foreground);
      outline: none;
      transition: border-color 0.2s;
    }
    
    input:focus, textarea:focus, select:focus {
      border-color: var(--primary);
    }
    
    /* Additional styles for component templates */
    .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
  </style>
</head>
<body class="bg-background">
  <div class="p-4 flex items-center justify-center min-h-screen">
    ${content}
  </div>
</body>
</html>`;
};

interface ComponentDialogProps {
  component: ComponentItem | null;
  onClose: () => void;
  copied: string | null;
  setCopied: (id: string | null) => void;
  previewMode: 'light' | 'dark';
  isFavorite: boolean;
  onToggleFavorite: () => void;
}

const CategoryBadge = ({ children, className }: { children: React.ReactNode, className?: string }) => {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className || ''}`}>
      {children}
    </span>
  );
};

const ComponentDialog: React.FC<ComponentDialogProps> = ({
  component,
  onClose,
  copied,
  setCopied,
  previewMode,
  isFavorite,
  onToggleFavorite
}) => {
  if (!component) return null;
  
  const copyToClipboard = () => {
    // Offer two copy options: component code or complete HTML
    navigator.clipboard.writeText(component.code);
    setCopied(component.id);
    toast.success("Code copied to clipboard!");
    setTimeout(() => {
      setCopied(null);
    }, 2000);
  };
  
  const copyCompleteHtml = () => {
    navigator.clipboard.writeText(generateCompleteHtml(component));
    setCopied(component.id);
    toast.success("Complete HTML copied to clipboard!");
    setTimeout(() => {
      setCopied(null);
    }, 2000);
  };

  // Complexity color based on level
  const complexityColors = {
    simple: "bg-green-500/10 text-green-500",
    medium: "bg-amber-500/10 text-amber-500",
    complex: "bg-red-500/10 text-red-500"
  };
  
  return (
    <Dialog open={!!component} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-4xl w-full p-0 overflow-hidden border border-primary/20 bg-black/80 backdrop-blur-xl flex flex-col h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="p-6 border-b border-border/40">
          <DialogTitle className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <span className="text-xl">{component.name}</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={(e) => {
                  e.stopPropagation(); 
                  e.preventDefault();
                  onToggleFavorite();
                }}
                className="flex items-center gap-1.5 px-3 h-8 hover:bg-primary/10 hover:text-primary"
              >
                {isFavorite ? (
                  <>
                    <Bookmark size={14} className="fill-primary text-primary" /> 
                    <span className="text-primary text-xs">Favorited</span>
                  </>
                ) : (
                  <>
                    <BookmarkPlus size={14} /> 
                    <span className="text-xs">Add to Favorites</span>
                  </>
                )}
              </Button>
              {component.tags && component.tags.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Tag size={12} className="text-primary" />
                  {component.tags.slice(0, 3).map((tag, index) => (
                    <React.Fragment key={tag}>
                      <span className="bg-secondary/30 px-1.5 py-0.5 rounded-md">{tag}</span>
                      {index < Math.min(component.tags.length, 3) - 1 && <span className="mx-1">•</span>}
                    </React.Fragment>
                  ))}
                  {component.tags.length > 3 && (
                    <span className="text-xs bg-secondary/30 px-1.5 py-0.5 rounded-md">+{component.tags.length - 3}</span>
                  )}
                </div>
              )}
              {component.createdAt && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar size={12} />
                  {new Date(component.createdAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            <div className="flex items-center justify-between">
              <div>View and copy the component code</div>
            </div>
          </DialogDescription>
          <div className="flex items-center gap-2 mt-3">
            {component.complexity && (
              <CategoryBadge className={`text-xs ${complexityColors[component.complexity]}`}>
                {component.complexity.charAt(0).toUpperCase() + component.complexity.slice(1)} complexity
              </CategoryBadge>
            )}
            <CategoryBadge className="text-xs bg-primary/5 border-primary/20 text-primary/80 transition-colors">
              {component.category.charAt(0).toUpperCase() + component.category.slice(1)}
            </CategoryBadge>
          </div>
        </DialogHeader>
        
        <Tabs defaultValue="preview" className="w-full flex flex-col flex-1 overflow-hidden">
          <div className="px-6 pt-4 pb-2 border-b border-border/40">
            <TabsList className="grid w-60 grid-cols-2">
              <TabsTrigger value="preview" className="flex items-center gap-1.5">
                Preview
              </TabsTrigger>
              <TabsTrigger value="code" className="flex items-center gap-1.5">
                <Code size={14} />
                Code
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="preview" className="px-6 py-6 flex-1 overflow-hidden relative">
            <div className="border border-border/50 rounded-lg h-full flex items-center justify-center overflow-hidden bg-secondary/30 relative">
              <div 
                className="flex items-center justify-center w-full h-full scale-90" 
                dangerouslySetInnerHTML={{ __html: component.preview }}
                onClick={(e) => {
                  // Prevent default behavior for all interactive elements inside preview
                  e.preventDefault();
                  
                  // Find all links and buttons in the preview
                  const clickableElements = e.currentTarget.querySelectorAll('a, button, [role="button"], input[type="submit"]');
                  
                  // Prevent their default behavior
                  clickableElements.forEach(element => {
                    element.addEventListener('click', (event) => {
                      event.preventDefault();
                      return false;
                    }, { capture: true });
                  });
                }} 
              />
            </div>
            <div className="absolute top-3 right-9">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1.5 bg-background/80 backdrop-blur-sm border-border"
                onClick={() => {
                  if (!component) return;
                  
                  // Create and dispatch event to add the component to the canvas
                  const event = new CustomEvent('vibecoding:add-to-canvas', { 
                    detail: { 
                      templateId: component.id, 
                      previewHtml: component.preview
                    } 
                  });
                  document.dispatchEvent(event);
                  
                  toast.success("Component added to Visual Canvas!", {
                    position: "bottom-right",
                    style: {
                      zIndex: 10000,
                      background: "rgba(34, 197, 94, 0.9)",
                      color: "white",
                    }
                  });
                  
                  // Close the dialog
                  onClose();
                }}
              >
                <Layout size={14} className="mr-1" />
                Use in Canvas
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="code" className="px-6 py-6 flex-1 overflow-hidden relative">
            <pre className="bg-secondary/20 border border-border/50 rounded-lg h-full overflow-auto p-6 text-sm font-mono whitespace-pre-wrap"
                 onClick={(e) => e.preventDefault()}>
              <code>{component.code}</code>
            </pre>
            <div className="absolute bottom-10 right-10">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyCompleteHtml} className="flex items-center gap-1.5">
                  {copied === component.id ? (
                    <>
                      <Check size={14} className="text-green-500" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      <span>Copy Complete HTML</span>
                    </>
                  )}
                </Button>
                <Button variant="default" size="sm" onClick={copyToClipboard} className="flex items-center gap-1.5">
                {copied === component.id ? (
                  <>
                      <Check size={14} />
                      <span>Copied!</span>
                  </>
                ) : (
                  <>
                      <Copy size={14} />
                      <span>Copy Code</span>
                  </>
                )}
              </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ComponentDialog;
